"use client";

import { useTranslations } from "use-intl";

import {
  BarChartIcon,
  DoubleChevronDownIcon,
  DoubleChevronUpIcon,
  InsightsIcon,
  TrendingUpIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";
import {
  displayDealPriceIndicators,
  type DealPriceIndicator,
} from "@garimpa/lib/notificationEmails";

interface PriceIndicatorMeta {
  icon: AppIcon;
  labelKey:
    | "priceIndicatorObservedLow"
    | "priceIndicatorObservedHigh"
    | "priceIndicatorAllTimeLow"
    | "priceIndicatorAllTimeHigh"
    | "priceIndicatorBelowAverage"
    | "priceIndicatorBelowTypical"
    | "priceIndicatorAboveAverage"
    | "priceIndicatorAboveTypical";
  className: string;
  textClassName: string;
}

const PRICE_INDICATOR_META: Record<DealPriceIndicator, PriceIndicatorMeta> = {
  observed_historical_low: {
    icon: DoubleChevronDownIcon,
    labelKey: "priceIndicatorObservedLow",
    className: "border-emerald-400/70 bg-emerald-400/5 text-emerald-400",
    textClassName: "text-emerald-400",
  },
  observed_historical_high: {
    icon: DoubleChevronUpIcon,
    labelKey: "priceIndicatorObservedHigh",
    className: "border-red-300/70 bg-red-300/5 text-red-300",
    textClassName: "text-red-300",
  },
  all_time_low: {
    icon: DoubleChevronDownIcon,
    labelKey: "priceIndicatorAllTimeLow",
    className: "border-emerald-400/70 bg-emerald-400/5 text-emerald-400",
    textClassName: "text-emerald-400",
  },
  all_time_high: {
    icon: DoubleChevronUpIcon,
    labelKey: "priceIndicatorAllTimeHigh",
    className: "border-red-300/70 bg-red-300/5 text-red-300",
    textClassName: "text-red-300",
  },
  below_average: {
    icon: BarChartIcon,
    labelKey: "priceIndicatorBelowAverage",
    className: "border-lime-300/70 bg-lime-300/5 text-lime-300",
    textClassName: "text-lime-300",
  },
  below_typical: {
    icon: InsightsIcon,
    labelKey: "priceIndicatorBelowTypical",
    className: "border-green-400/70 bg-green-400/5 text-green-400",
    textClassName: "text-green-400",
  },
  above_average: {
    icon: BarChartIcon,
    labelKey: "priceIndicatorAboveAverage",
    className: "border-yellow-300/70 bg-yellow-300/5 text-yellow-300",
    textClassName: "text-yellow-300",
  },
  above_typical: {
    icon: TrendingUpIcon,
    labelKey: "priceIndicatorAboveTypical",
    className: "border-orange-300/70 bg-orange-300/5 text-orange-300",
    textClassName: "text-orange-300",
  },
};

/** The single highest-priority indicator as plain data, for text (non-chip) presentations. */
export function topPriceIndicator(indicators: string[] | null | undefined) {
  const [indicator] = displayDealPriceIndicators(indicators);
  if (!indicator) return null;
  const meta = PRICE_INDICATOR_META[indicator];
  return { icon: meta.icon, labelKey: meta.labelKey, textClassName: meta.textClassName };
}

export function priceIndicatorTextClass(indicators: string[] | null | undefined): string | null {
  const [indicator] = displayDealPriceIndicators(indicators);
  return indicator ? PRICE_INDICATOR_META[indicator].textClassName : null;
}

export function PriceIndicatorChips({
  className,
  indicators,
  size = "default",
}: {
  className?: string;
  indicators: string[] | null | undefined;
  size?: "compact" | "default" | "responsive";
}) {
  const t = useTranslations("shared.affiliates");
  const orderedIndicators = displayDealPriceIndicators(indicators);

  if (orderedIndicators.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {orderedIndicators.map((indicator) => {
        const meta = PRICE_INDICATOR_META[indicator];
        const Icon = meta.icon;
        const label = t(meta.labelKey);
        return (
          <span
            key={indicator}
            aria-label={label}
            title={label}
            className={cn(
              "box-border inline-flex items-center font-bold leading-none",
              size === "compact"
                ? "min-h-6 shrink-0 gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px]"
                : size === "responsive"
                  ? "min-h-5 min-w-0 max-w-full text-[11px]"
                  : "min-h-7 shrink-0 gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-[11px]",
              size === "responsive" ? meta.textClassName : meta.className,
            )}
          >
            {size !== "responsive" && (
              <Icon
                className={cn("leading-none", size === "compact" ? "text-[13px]" : "text-sm")}
              />
            )}
            <span className={size === "responsive" ? "truncate" : undefined}>{label}</span>
          </span>
        );
      })}
    </div>
  );
}
